/**
 * @file scoutReportAutoTranslate.ts
 * @description Decides whether opening a scout report should automatically
 * translate it into the viewer's language, and into which locale (SE-6510).
 *
 * The whole point of this module is that "the viewer's language" is not a
 * two-state question. A viewer is in exactly one of these situations, and the
 * product owes a different behaviour to each:
 *
 * 1. **Not yet known.** `me` has not resolved. Reading `languagePreference`
 *    here yields `undefined`, which `resolveDisplayLocale` would happily turn
 *    into `en_US` — a *fabricated* answer that looks identical to a genuine
 *    English preference and would make a French viewer's report open in
 *    English for good. This is `pending`: decide nothing, fire nothing.
 * 2. **A language we can serve.** One of the offered six, or a sibling of one
 *    (`es_MX` → `es_ES`). This is the only case that translates.
 * 3. **A language we cannot serve** — stranded on one of the fourteen dropped
 *    locales with no sibling in the six (`ja_JP`), or with no stored
 *    preference at all. There is no translation to offer, so the report stays
 *    in its authored language. Crucially this is NOT reported as "English is
 *    your language"; the caller is told the preference is not offered, so it
 *    never renders a claim about the viewer it cannot support.
 * @module reports
 */
import {
  DEFAULT_UI_LOCALE,
  findSupportedUiLocale,
  type UiLocale,
} from "@/constants/locales";
import { SCOUT_REPORT_AUTO_TRANSLATE } from "@/config/featureFlags";

/** Why a report will not be auto-translated. Each reason is a distinct fact. */
export type ScoutReportAutoTranslateSkipReason =
  /** The SE-6510 flag is off. Nothing about the viewer was consulted. */
  | "disabled"
  /** Nobody is signed in, so there is no preference to honour. */
  | "no-viewer"
  /** The viewer is known and has no stored language preference. */
  | "no-preference"
  /**
   * The viewer's preference is real but outside the offered six and has no
   * sibling in them. We cannot serve their language — and must not pretend the
   * default is it.
   */
  | "preference-not-offered"
  /**
   * The viewer's language is the one the backend already assumes the report was
   * authored in, so a translation round-trip would buy nothing (PRD amendment
   * D2). This is the majority case, and skipping it is what keeps a Bedrock
   * invocation off every report open.
   */
  | "already-authored-language";

/**
 * The decision for one (viewer, report-open) pair. A discriminated union rather
 * than a nullable locale so a caller cannot silently collapse "we do not know
 * yet" into "no translation needed".
 */
export type ScoutReportAutoTranslateDecision =
  | { readonly kind: "pending" }
  | {
      readonly kind: "skip";
      readonly reason: ScoutReportAutoTranslateSkipReason;
    }
  | { readonly kind: "translate"; readonly locale: UiLocale };

/** Inputs to {@link resolveScoutReportAutoTranslate}. */
export interface ScoutReportAutoTranslateInput {
  /** Whether the SE-6510 flag permits automatic translation at all. */
  readonly isEnabled: boolean;
  /**
   * Whether the viewer's identity has resolved. `false` means the answer is not
   * available yet — NOT that the viewer is anonymous.
   */
  readonly isViewerKnown: boolean;
  /** Whether anyone is signed in. Only consulted once the viewer is known. */
  readonly isSignedIn: boolean;
  /** The signed-in viewer's stored `languagePreference`, if any. */
  readonly preference: string | null | undefined;
}

/**
 * Build-time state of the scout-report auto-translate flag.
 *
 * Read through a function rather than at module scope so a test can flip
 * `process.env` between cases, mirroring the i18n kill-switch — while staying a
 * completely separate switch from it.
 * @returns Whether opening a report may automatically translate it
 */
export function isScoutReportAutoTranslateEnabled(): boolean {
  const raw = process.env.EXPO_PUBLIC_SCOUT_REPORT_AUTO_TRANSLATE;
  const normalized = raw?.trim().toLowerCase();
  if (normalized === "0" || normalized === "false" || normalized === "off") {
    return false;
  }
  if (normalized === "1" || normalized === "true" || normalized === "on") {
    return true;
  }
  return SCOUT_REPORT_AUTO_TRANSLATE === "on";
}

/**
 * Resolves what opening a scout report should do about the viewer's language.
 *
 * Order matters. The flag is checked first so a disabled build consults nothing
 * about the viewer; readiness is checked before the preference so an unresolved
 * `me` can never be mistaken for an English one.
 * @param input - Flag state, viewer readiness, and the stored preference
 * @returns The decision — pending, a reasoned skip, or a locale to translate into
 */
export function resolveScoutReportAutoTranslate(
  input: ScoutReportAutoTranslateInput
): ScoutReportAutoTranslateDecision {
  if (!input.isEnabled) return { kind: "skip", reason: "disabled" };
  if (!input.isViewerKnown) return { kind: "pending" };
  if (!input.isSignedIn) return { kind: "skip", reason: "no-viewer" };
  if (!input.preference) return { kind: "skip", reason: "no-preference" };

  const offered = findSupportedUiLocale(input.preference);
  if (offered === undefined) {
    return { kind: "skip", reason: "preference-not-offered" };
  }
  if (offered === DEFAULT_UI_LOCALE) {
    return { kind: "skip", reason: "already-authored-language" };
  }
  return { kind: "translate", locale: offered };
}
